import React, { useState, useRef, useEffect } from 'react';
import {
  Typography, Box, Card, Button, Switch,
  FormControlLabel, Tabs, Tab, Divider, 
  List, ListItem, ListItemText, Alert, Snackbar, Avatar,
  InputLabel, Skeleton, LinearProgress, CircularProgress
} from '@mui/material';
import { useSettings } from '../../contexts/SettingsContext';
import { useAuth } from '../../contexts/AuthContext'; // 🔐 Import du contexte d'authentification
import {
  Business as BusinessIcon,
  AttachMoney as AttachMoneyIcon,
  Security as SecurityIcon,
  Save as SaveIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

const Parametres = () => {
  const { settings, setSetting, uploadLogo, removeLogo, loading } = useSettings();
  const { user } = useAuth(); // 🔐 Utilisation du contexte d'authentification
  const [activeTab, setActiveTab] = useState(0);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [editMode, setEditMode] = useState({
    agency: false
  });
  
  // 🔧 DEBUG: Vérifier l'état editMode
  useEffect(() => {
    console.log('🔧 EditMode state changed:', editMode);
  }, [editMode]);
  const [passwordError, setPasswordError] = useState('');
  
  // 🎨 STYLES CSS PERSONNALISÉS POUR CORRIGER LES PROBLÈMES DE HOVER
  const customInputStyles = {
    width: '100%',
    padding: '14px 16px',
    borderRadius: '8px',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    fontFamily: 'inherit',
    fontSize: '16px',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    outline: 'none',
    color: '#fff',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
      borderColor: 'rgba(255, 255, 255, 0.3)',
      color: '#fff !important' // Force la couleur blanche
    },
    '&:focus': {
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
      borderColor: 'rgba(37, 99, 235, 0.5)',
      color: '#fff !important' // Force la couleur blanche
    },
    '&:disabled': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderColor: 'rgba(255, 255, 255, 0.1)',
      color: 'rgba(255, 255, 255, 0.5)',
      cursor: 'not-allowed'
    }
  };
  
  // Define SkeletonInput component for progressive loading
  const SkeletonInput = ({ height = 40 }) => (
    <Skeleton variant="rectangular" height={height} sx={{ borderRadius: 2, width: '100%', mb: 2 }} />
  );
  
  // Create refs for form inputs
  const nameRef = useRef(null);
  const emailRef = useRef(null);
  const phoneRef = useRef(null);
  const websiteRef = useRef(null);
  const licenseRef = useRef(null);
  const addressRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // Initialize refs with current settings on component mount
  useEffect(() => {
    if (nameRef.current) nameRef.current.value = settings.agencyName || '';
    if (emailRef.current) emailRef.current.value = settings.agencyEmail || '';
    if (phoneRef.current) phoneRef.current.value = settings.agencyPhone || '';
    if (websiteRef.current) websiteRef.current.value = settings.agencyWebsite || '';
    if (licenseRef.current) licenseRef.current.value = settings.businessLicense || '';
    if (addressRef.current) addressRef.current.value = settings.agencyAddress || '';
  }, [settings]);

  // Handle logo upload
  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setLogoUploading(true);
    
    try {
      await uploadLogo(file);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    } catch (error) {
      console.error('Error uploading logo:', error);
    } finally {
      setLogoUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  const handleLogoRemove = () => {
    removeLogo();
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 3000);
  };

  // Toggle edit mode
  const toggleEditMode = (section) => {
    console.log('🔧 Toggle Edit Mode:', section, 'Current state:', editMode[section]);
    setEditMode(prev => {
      const newState = { ...prev, [section]: !prev[section] };
      console.log('🔧 New edit state:', newState);
      
      // If we're exiting edit mode, save changes
      if (prev[section] && !newState[section]) {
        if (section === 'agency') {
          console.log('💾 Saving agency changes...');
          // Get values from refs
          const agencyData = {
            agencyName: nameRef.current?.value || '',
            agencyEmail: emailRef.current?.value || '',
            agencyPhone: phoneRef.current?.value || '',
            agencyAddress: addressRef.current?.value || '',
            agencyWebsite: websiteRef.current?.value || '',
            businessLicense: licenseRef.current?.value || ''
          };
          console.log('💾 Agency data to save:', agencyData);
          
          setSetting('agencyName', agencyData.agencyName);
          setSetting('agencyEmail', agencyData.agencyEmail);
          setSetting('agencyPhone', agencyData.agencyPhone);
          setSetting('agencyAddress', agencyData.agencyAddress);
          setSetting('agencyWebsite', agencyData.agencyWebsite);
          setSetting('businessLicense', agencyData.businessLicense);
          
          setShowSuccessMessage(true);
          setTimeout(() => setShowSuccessMessage(false), 3000);
        }
      }
      
      return newState;
    });
  };



  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
  
  // Custom Input component using native HTML elements
  const CustomInput = ({ label, defaultValue, inputRef, disabled, multiline = false, placeholder }) => {
    // 🔧 DEBUG: Vérifier les props reçues
    console.log(`🔧 CustomInput "${label}":`, { disabled, defaultValue, placeholder });
    
    return (
      <Box sx={{ mb: 2 }}>
        <InputLabel 
          sx={{ 
            mb: 1, 
            fontWeight: 500, 
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: '0.875rem'
          }}
        >
          {label}
        </InputLabel>
      {multiline ? (
        <textarea
          ref={inputRef}
          defaultValue={defaultValue}
          disabled={disabled}
          placeholder={placeholder}
          rows={3}
          style={{
            width: '100%',
            padding: '12px 16px',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            fontFamily: 'inherit',
            fontSize: '1rem',
            backgroundColor: disabled ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.1)',
            color: '#fff',
            resize: 'vertical',
            outline: 'none'
          }}
        />
      ) : (
        <input
          ref={inputRef}
          defaultValue={defaultValue}
          disabled={disabled}
          placeholder={placeholder}
          style={{
            width: '100%',
            padding: '12px 16px',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            fontFamily: 'inherit',
            fontSize: '1rem',
            backgroundColor: disabled ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.1)',
            color: '#fff',
            outline: 'none'
          }}
        />
      )}
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#fff', mb: 1 }}>
            Paramètres
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
            Configurez les paramètres essentiels de votre agence
          </Typography>
        </Box>
        <Button
          variant="contained"
          sx={{
            background: 'linear-gradient(to right, #2563eb, #9333ea)',
            color: '#fff',
            px: 3,
            py: 1.5,
            borderRadius: 3,
            fontWeight: 600,
            '&:hover': {
              background: 'linear-gradient(to right, #1e40af, #7e22ce)',
            }
          }}
        >
          <SaveIcon sx={{ mr: 1 }} />
          Enregistrer
        </Button>
      </Box>

      {/* Configuration tabs */}
      <Card sx={{
        background: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(20px)',
        borderRadius: '24px',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        overflow: 'hidden'
      }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            p: 1,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem',
              color: 'rgba(255, 255, 255, 0.7)',
              borderRadius: '16px',
              mx: 0.5,
              '&.Mui-selected': {
                color: '#fff',
                background: 'linear-gradient(to right, #2563eb, #9333ea)',
              }
            },
            '& .MuiTabs-indicator': {
              display: 'none',
            }
          }}
          variant="fullWidth"
        >
          <Tab icon={<BusinessIcon />} label="Agence" />
          <Tab icon={<AttachMoneyIcon />} label="Tarification" />
          <Tab icon={<SecurityIcon />} label="Profil Utilisateur" />
        </Tabs>

        {/* Agency Information Tab */}
        <TabPanel value={activeTab} index={0}>
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#fff' }}>
                Informations de l'Agence
              </Typography>
              <Button
                variant={editMode.agency ? "contained" : "outlined"}
                onClick={() => {
                  console.log('🔧 Bouton Modifier cliqué!');
                  toggleEditMode('agency');
                }}
                startIcon={editMode.agency ? <SaveIcon /> : null}
                sx={{ 
                  borderRadius: '12px',
                  px: 2,
                  py: 1,
                  background: editMode.agency ? 'linear-gradient(to right, #2563eb, #9333ea)' : 'transparent',
                  border: editMode.agency ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#fff',
                  '&:hover': {
                    background: editMode.agency ? 'linear-gradient(to right, #1e40af, #7e22ce)' : 'rgba(255, 255, 255, 0.1)',
                  }
                }}
              >
                {editMode.agency ? 'Enregistrer' : 'Modifier'}
              </Button>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 3, mb: 4, flexDirection: { xs: 'column', md: 'row' } }}>
              {/* Logo section */}
              <Box sx={{ width: { xs: '100%', md: '200px' }, textAlign: 'center' }}>
                {loading ? (
                  <Skeleton variant="circular" width={120} height={120} sx={{ mx: 'auto', mb: 2 }} />
                ) : settings.agencyLogo ? (
                  <Avatar
                    src={settings.agencyLogo}
                    sx={{
                      width: 120,
                      height: 120,
                      mx: 'auto',
                      mb: 2,
                      border: '3px solid rgba(255, 255, 255, 0.2)',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
                    }}
                  />
                ) : (
                  <Avatar
                    sx={{
                      width: 120,
                      height: 120,
                      mx: 'auto',
                      mb: 2,
                      background: 'linear-gradient(135deg, #2563eb 0%, #9333ea 100%)',
                      fontSize: '2rem',
                      fontWeight: 700,
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
                    }}
                  >
                    {settings.agencyName ? settings.agencyName.split(' ').map(word => word[0]).join('').slice(0, 3) : '?'}
                  </Avatar>
                )}
                
                {/* Hidden file input */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleLogoUpload}
                  accept="image/*"
                  style={{ display: 'none' }}
                />
                
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                  {/* Upload / Change Button */}
                  {loading ? (
                    <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: '12px' }} />
                  ) : (
                    <Button
                      variant="contained"
                      startIcon={<CloudUploadIcon />}
                      onClick={triggerFileInput}
                      disabled={logoUploading}
                      size="small"
                      sx={{ 
                        borderRadius: '12px',
                        background: settings.agencyLogo ? 'rgba(255, 255, 255, 0.1)' : 'linear-gradient(to right, #2563eb, #9333ea)',
                        color: '#fff',
                        border: settings.agencyLogo ? '1px solid rgba(255, 255, 255, 0.2)' : 'none',
                        '&:hover': {
                          background: settings.agencyLogo ? 'rgba(255, 255, 255, 0.2)' : 'linear-gradient(to right, #1e40af, #7e22ce)',
                        }
                      }}
                    >
                      {logoUploading ? 'Chargement...' : settings.agencyLogo ? 'Changer' : 'Logo'}
                    </Button>
                  )}

                  {/* Delete Button (only when a logo exists) */}
                  {loading ? (
                    settings.agencyLogo && (
                      <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: '12px' }} />
                    )
                  ) : (
                    settings.agencyLogo && (
                      <Button
                        variant="outlined"
                        startIcon={<DeleteIcon />}
                        onClick={handleLogoRemove}
                        size="small"
                        sx={{ 
                          borderRadius: '12px',
                          border: '1px solid rgba(255, 0, 0, 0.3)',
                          color: 'rgba(255, 100, 100, 0.8)',
                          '&:hover': {
                            background: 'rgba(255, 0, 0, 0.1)',
                            border: '1px solid rgba(255, 0, 0, 0.5)',
                          }
                        }}
                      >
                        Supprimer
                      </Button>
                    )
                  )}
                </Box>
                
                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'rgba(255, 255, 255, 0.5)' }}>
                  Formats: JPG, PNG, GIF (max 5MB)
                </Typography>
              </Box>
              
              {/* Agency details form */}
              <Box sx={{ flex: 1 }}>
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
                  gap: 2
                }}>
                  <Box sx={{ gridColumn: { xs: '1', sm: '1 / 3' } }}>
                    {loading ? (
                      <SkeletonInput />
                    ) : (
                    <CustomInput
                      label="Nom de l'agence"
                      defaultValue={settings.agencyName}
                      inputRef={nameRef}
                      disabled={!editMode.agency}
                      placeholder="Entrez le nom de votre agence"
                    />
                    )}
                  </Box>
                  
                  {loading ? (
                    <SkeletonInput />
                  ) : (
                    <CustomInput
                      label="Email"
                      defaultValue={settings.agencyEmail}
                      inputRef={emailRef}
                      disabled={!editMode.agency}
                      placeholder="contact@votreagence.com"
                    />
                  )}
                  
                  {loading ? (
                    <SkeletonInput />
                  ) : (
                    <CustomInput
                      label="Téléphone"
                      defaultValue={settings.agencyPhone}
                      inputRef={phoneRef}
                      disabled={!editMode.agency}
                      placeholder="+213 XX XXX XXXX"
                    />
                  )}
                  
                  {loading ? (
                    <SkeletonInput />
                  ) : (
                    <CustomInput
                      label="Site Web"
                      defaultValue={settings.agencyWebsite}
                      inputRef={websiteRef}
                      disabled={!editMode.agency}
                      placeholder="www.votreagence.com"
                    />
                  )}
                  
                  {loading ? (
                    <SkeletonInput />
                  ) : (
                    <CustomInput
                      label="Numéro de Licence"
                      defaultValue={settings.businessLicense}
                      inputRef={licenseRef}
                      disabled={!editMode.agency}
                      placeholder="AGV-DZ-XXXX-XXX"
                    />
                  )}
                  
                  <Box sx={{ gridColumn: { xs: '1', sm: '1 / 3' } }}>
                    {loading ? (
                      <SkeletonInput height={80} />
                    ) : (
                      <CustomInput
                        label="Adresse"
                        defaultValue={settings.agencyAddress}
                        inputRef={addressRef}
                        disabled={!editMode.agency}
                        placeholder="Entrez l'adresse complète de votre agence"
                        multiline
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>

            <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
            
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.9)', mb: 2 }}>
                Logiciel de Gestion (non modifiable)
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, mr: 1, minWidth: 120, color: 'rgba(255, 255, 255, 0.7)' }}>
                  Nom du logiciel:
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                  {settings.appName || 'TravelIT'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, mr: 1, minWidth: 120, color: 'rgba(255, 255, 255, 0.7)' }}>
                  Version:
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                  {settings.appVersion || '1.0.0'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </TabPanel>

        {/* Pricing Settings Tab */}
        <TabPanel value={activeTab} index={1}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#fff' }}>
              Paramètres de Tarification
            </Typography>
            
            <List>
              <ListItem 
                disableRipple 
                disableTouchRipple
                sx={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.1)', 
                  borderRadius: '16px', 
                  mb: 2,
                  p: 3,
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1) !important'
                  }
                }}>
                <ListItemText
                  primary={<Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 500 }}>Devise par défaut</Typography>}
                  secondary={<Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>Devise utilisée pour les prix</Typography>}
                />
                <Box>
                  <Button 
                    variant={settings.defaultCurrency === 'DZD' ? "contained" : "outlined"}
                    onClick={() => setSetting('defaultCurrency', 'DZD')}
                    sx={{ 
                      mr: 1, 
                      borderRadius: '12px',
                      background: settings.defaultCurrency === 'DZD' ? 'linear-gradient(to right, #2563eb, #9333ea)' : 'transparent',
                      border: settings.defaultCurrency === 'DZD' ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
                      color: '#fff',
                      '&:hover': {
                        background: settings.defaultCurrency === 'DZD' ? 'linear-gradient(to right, #1e40af, #7e22ce)' : 'rgba(255, 255, 255, 0.02)',
                      }
                    }}
                  >
                    DZD
                  </Button>
                  <Button 
                    variant={settings.defaultCurrency === 'EUR' ? "contained" : "outlined"}
                    onClick={() => setSetting('defaultCurrency', 'EUR')}
                    sx={{ 
                      mr: 1, 
                      borderRadius: '12px',
                      background: settings.defaultCurrency === 'EUR' ? 'linear-gradient(to right, #2563eb, #9333ea)' : 'transparent',
                      border: settings.defaultCurrency === 'EUR' ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
                      color: '#fff',
                      '&:hover': {
                        background: settings.defaultCurrency === 'EUR' ? 'linear-gradient(to right, #1e40af, #7e22ce)' : 'rgba(255, 255, 255, 0.02)',
                      }
                    }}
                  >
                    EUR
                  </Button>
                  <Button 
                    variant={settings.defaultCurrency === 'USD' ? "contained" : "outlined"}
                    onClick={() => setSetting('defaultCurrency', 'USD')}
                    sx={{ 
                      borderRadius: '12px',
                      background: settings.defaultCurrency === 'USD' ? 'linear-gradient(to right, #2563eb, #9333ea)' : 'transparent',
                      border: settings.defaultCurrency === 'USD' ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
                      color: '#fff',
                      '&:hover': {
                        background: settings.defaultCurrency === 'USD' ? 'linear-gradient(to right, #1e40af, #7e22ce)' : 'rgba(255, 255, 255, 0.02)',
                      }
                    }}
                  >
                    USD
                  </Button>
                </Box>
              </ListItem>

              {/* Exchange Rates Section - Simplified */}
              <ListItem 
                disableRipple 
                disableTouchRipple
                sx={{ 
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)', 
                  borderRadius: '16px', 
                  mb: 2,
                  p: 3,
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1) !important'
                  }
                }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#fff' }}>
                    Taux de change (Marché noir)
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                    Dernière mise à jour: {settings.exchangeRates?.EUR?.lastUpdated ? 
                      new Date(settings.exchangeRates.EUR.lastUpdated).toLocaleDateString('fr-FR') : 
                      'Jamais'}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, width: '100%' }}>
                  {/* EUR Rates */}
                  <Box sx={{ flex: 1, border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '12px', p: 3, backgroundColor: 'rgba(255, 255, 255, 0.05)' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#fff' }}>
                      Euro (EUR)
                    </Typography>
                    
                    <Box>
                      <InputLabel htmlFor="eur-rate" sx={{ 
                        fontSize: '0.75rem', 
                        mb: 0.5, 
                        color: 'rgba(255, 255, 255, 0.7)',
                        '&.Mui-focused': {
                          color: 'rgba(255, 255, 255, 0.9)'
                        }
                      }}>
                        Taux de change (1 EUR = ? DZD)
                      </InputLabel>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <input
                          id="eur-rate"
                          type="number"
                          value={settings.exchangeRates?.EUR?.rate || ''}
                          onChange={(e) => {
                            const value = parseFloat(e.target.value);
                            if (!isNaN(value)) {
                              setSetting('exchangeRates', {
                                ...settings.exchangeRates,
                                EUR: {
                                  ...settings.exchangeRates?.EUR,
                                  rate: value,
                                  lastUpdated: new Date().toISOString()
                                }
                              });
                            }
                          }}
                          style={{
                            width: '120px',
                            padding: '10px 12px',
                            borderRadius: '8px',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            color: '#fff'
                          }}
                        />
                        <Typography sx={{ ml: 1, color: '#fff' }}>DZD</Typography>
                      </Box>
                    </Box>
                  </Box>
                  
                  {/* USD Rates */}
                  <Box sx={{ flex: 1, border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '12px', p: 3, backgroundColor: 'rgba(255, 255, 255, 0.05)' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#fff' }}>
                      Dollar US (USD)
                    </Typography>
                    
                    <Box>
                      <InputLabel htmlFor="usd-rate" sx={{ 
                        fontSize: '0.75rem', 
                        mb: 0.5, 
                        color: 'rgba(255, 255, 255, 0.7)',
                        '&.Mui-focused': {
                          color: 'rgba(255, 255, 255, 0.9)'
                        }
                      }}>
                        Taux de change (1 USD = ? DZD)
                      </InputLabel>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <input
                          id="usd-rate"
                          type="number"
                          value={settings.exchangeRates?.USD?.rate || ''}
                          onChange={(e) => {
                            const value = parseFloat(e.target.value);
                            if (!isNaN(value)) {
                              setSetting('exchangeRates', {
                                ...settings.exchangeRates,
                                USD: {
                                  ...settings.exchangeRates?.USD,
                                  rate: value,
                                  lastUpdated: new Date().toISOString()
                                }
                              });
                            }
                          }}
                          style={{
                            width: '120px',
                            padding: '10px 12px',
                            borderRadius: '8px',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            color: '#fff'
                          }}
                        />
                        <Typography sx={{ ml: 1, color: '#fff' }}>DZD</Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
                
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'rgba(255, 255, 255, 0.5)' }}>
                    Ces taux seront appliqués automatiquement pour les conversions de devise lors des transactions et paiements.
                  </Typography>
                </Box>
              </ListItem>

              <ListItem 
                disableRipple 
                disableTouchRipple
                sx={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.1)', 
                  borderRadius: '16px', 
                  mb: 2,
                  p: 3,
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1) !important'
                  }
                }}>
                <ListItemText
                  primary={<Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 500 }}>Taux de TVA</Typography>}
                  secondary={<Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>Pourcentage de TVA appliqué</Typography>}
                />
                <Box>
                  <Button 
                    variant={settings.taxRate === 19 ? "contained" : "outlined"}
                    onClick={() => setSetting('taxRate', 19)}
                    sx={{ 
                      mr: 1, 
                      borderRadius: '12px',
                      background: settings.taxRate === 19 ? 'linear-gradient(to right, #2563eb, #9333ea)' : 'transparent',
                      border: settings.taxRate === 19 ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
                      color: '#fff',
                      '&:hover': {
                        background: settings.taxRate === 19 ? 'linear-gradient(to right, #1e40af, #7e22ce)' : 'rgba(255, 255, 255, 0.02)',
                      }
                    }}
                  >
                    19%
                  </Button>
                  <Button 
                    variant={settings.taxRate === 9 ? "contained" : "outlined"}
                    onClick={() => setSetting('taxRate', 9)}
                    sx={{ 
                      mr: 1, 
                      borderRadius: '12px',
                      background: settings.taxRate === 9 ? 'linear-gradient(to right, #2563eb, #9333ea)' : 'transparent',
                      border: settings.taxRate === 9 ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
                      color: '#fff',
                      '&:hover': {
                        background: settings.taxRate === 9 ? 'linear-gradient(to right, #1e40af, #7e22ce)' : 'rgba(255, 255, 255, 0.02)',
                      }
                    }}
                  >
                    9%
                  </Button>
                  <Button 
                    variant={settings.taxRate === 0 ? "contained" : "outlined"}
                    onClick={() => setSetting('taxRate', 0)}
                    sx={{ 
                      borderRadius: '12px',
                      background: settings.taxRate === 0 ? 'linear-gradient(to right, #2563eb, #9333ea)' : 'transparent',
                      border: settings.taxRate === 0 ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
                      color: '#fff',
                      '&:hover': {
                        background: settings.taxRate === 0 ? 'linear-gradient(to right, #1e40af, #7e22ce)' : 'rgba(255, 255, 255, 0.02)',
                      }
                    }}
                  >
                    0%
                  </Button>
                </Box>
              </ListItem>

              <ListItem 
                disableRipple 
                disableTouchRipple
                sx={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.1)', 
                  borderRadius: '16px', 
                  mb: 2,
                  p: 3,
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1) !important'
                  }
                }}>
                <ListItemText
                  primary={<Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 500 }}>Commission par défaut</Typography>}
                  secondary={<Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>Pourcentage de commission sur les ventes</Typography>}
                />
                <Box>
                  <Button 
                    variant={settings.commissionRate === 10 ? "contained" : "outlined"}
                    onClick={() => setSetting('commissionRate', 10)}
                    sx={{ 
                      mr: 1, 
                      borderRadius: '12px',
                      background: settings.commissionRate === 10 ? 'linear-gradient(to right, #2563eb, #9333ea)' : 'transparent',
                      border: settings.commissionRate === 10 ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
                      color: '#fff',
                      '&:hover': {
                        background: settings.commissionRate === 10 ? 'linear-gradient(to right, #1e40af, #7e22ce)' : 'rgba(255, 255, 255, 0.02)',
                      }
                    }}
                  >
                    10%
                  </Button>
                  <Button 
                    variant={settings.commissionRate === 15 ? "contained" : "outlined"}
                    onClick={() => setSetting('commissionRate', 15)}
                    sx={{ 
                      mr: 1, 
                      borderRadius: '12px',
                      background: settings.commissionRate === 15 ? 'linear-gradient(to right, #2563eb, #9333ea)' : 'transparent',
                      border: settings.commissionRate === 15 ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
                      color: '#fff',
                      '&:hover': {
                        background: settings.commissionRate === 15 ? 'linear-gradient(to right, #1e40af, #7e22ce)' : 'rgba(255, 255, 255, 0.02)',
                      }
                    }}
                  >
                    15%
                  </Button>
                  <Button 
                    variant={settings.commissionRate === 20 ? "contained" : "outlined"}
                    onClick={() => setSetting('commissionRate', 20)}
                    sx={{ 
                      borderRadius: '12px',
                      background: settings.commissionRate === 20 ? 'linear-gradient(to right, #2563eb, #9333ea)' : 'transparent',
                      border: settings.commissionRate === 20 ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
                      color: '#fff',
                      '&:hover': {
                        background: settings.commissionRate === 20 ? 'linear-gradient(to right, #1e40af, #7e22ce)' : 'rgba(255, 255, 255, 0.02)',
                      }
                    }}
                  >
                    20%
                  </Button>
                </Box>
              </ListItem>
            </List>
          </Box>
        </TabPanel>

        {/* Profile Tab */}
        <TabPanel value={activeTab} index={2}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#fff' }}>
              Profil Utilisateur
            </Typography>
            
            <Box sx={{ 
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: { xs: 3, md: 4 },
              mb: { xs: 3, md: 4 }
            }}>
              {/* User Avatar Section */}
              <Box sx={{ 
                width: { xs: '100%', md: '250px' },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mb: { xs: 1, md: 0 }
              }}>
                <Avatar
                  src={null}
                  sx={{
                    width: { xs: 120, sm: 140, md: 150 },
                    height: { xs: 120, sm: 140, md: 150 },
                    mb: { xs: 1.5, md: 2 },
                    fontSize: { xs: '2.5rem', md: '3rem' },
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #2563eb 0%, #9333ea 100%)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                    border: '3px solid rgba(255, 255, 255, 0.2)'
                  }}
                >
                  {user?.username?.charAt(0)?.toUpperCase() || 'A'}
                </Avatar>
                
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    mb: 0.5,
                    fontSize: { xs: '1.125rem', md: '1.25rem' },
                    color: 'white'
                  }}
                >
                  {user?.username || 'Utilisateur'}
                </Typography>
                
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mb: { xs: 1.5, md: 2 },
                    fontSize: { xs: '0.8125rem', md: '0.875rem' },
                    color: 'rgba(255, 255, 255, 0.7)'
                  }}
                >
                  {'Administrateur'}
                </Typography>
                
                <Box sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  width: '100%',
                  p: { xs: 1.5, md: 2 },
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: { xs: '8px', md: '12px' },
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                }}>
                                      <Typography 
                      variant="subtitle2" 
                      sx={{ 
                        fontWeight: 600, 
                        mb: 1,
                        fontSize: { xs: '0.75rem', md: '0.875rem' },
                        color: '#fff'
                      }}
                    >
                      Informations de connexion
                    </Typography>
                  
                  <Box sx={{ width: '100%', mb: 1 }}>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontSize: { xs: '0.65rem', md: '0.75rem' },
                        color: 'rgba(255, 255, 255, 0.7)'
                      }}
                    >
                      Nom d'utilisateur
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 500,
                        fontSize: { xs: '0.75rem', md: '0.875rem' },
                        wordBreak: 'break-word',
                        color: '#fff'
                      }}
                    >
                      {user?.username || 'Utilisateur'}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ width: '100%', mb: 0 }}>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontSize: { xs: '0.65rem', md: '0.75rem' },
                        color: 'rgba(255, 255, 255, 0.7)'
                      }}
                    >
                      Dernière connexion
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 500,
                        fontSize: { xs: '0.75rem', md: '0.875rem' },
                        color: '#fff'
                      }}
                    >
                      {new Date().toLocaleDateString('fr-FR', { 
                        day: 'numeric', 
                        month: 'long', 
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              
              {/* Password Change Section */}
              <Box sx={{ flex: 1 }}>
                <Box sx={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.1)', 
                  borderRadius: { xs: '8px', md: '16px' }, 
                  p: { xs: 2, md: 3 },
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  mb: { xs: 2, md: 3 }
                }}>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      fontWeight: 600, 
                      mb: { xs: 2, md: 3 },
                      fontSize: { xs: '1rem', md: '1.125rem' },
                      color: '#fff'
                    }}
                  >
                    Modifier vos informations
                  </Typography>
                  
                  <form>
                    <Box sx={{ 
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      mb: 3,
                      maxWidth: { xs: '100%', sm: '350px' }
                    }}>
                      <Box>
                        <InputLabel 
                          htmlFor="username" 
                          sx={{ 
                            mb: 0.5, 
                            fontWeight: 500, 
                            color: 'rgba(255, 255, 255, 0.7)',
                            fontSize: { xs: '0.75rem', md: '0.875rem' }
                          }}
                        >
                          Nom d'utilisateur
                        </InputLabel>
                        <input
                          id="username"
                          name="username"
                          type="text"
                          defaultValue={user?.username || ''}
                          style={customInputStyles}
                        />
                      </Box>
                    </Box>
                    
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: { xs: 'center', sm: 'flex-end' }
                    }}>
                  <Button 
                        type="button"
                        variant="contained"
                        onClick={() => {
                          // Persist via backend in future; UI-only feedback now
                          setShowSuccessMessage(true);
                          setTimeout(() => setShowSuccessMessage(false), 3000);
                        }}
                        sx={{ 
                          px: { xs: 4, md: 3 },
                          py: { xs: 1.5, md: 1 },
                          borderRadius: '8px',
                          fontWeight: 600,
                          minWidth: { xs: '200px', md: 'auto' },
                          fontSize: { xs: '0.9375rem', md: 'inherit' },
                          background: 'linear-gradient(to right, #2563eb, #9333ea)',
                          '&:hover': {
                            background: 'linear-gradient(to right, #1e40af, #7e22ce)',
                          }
                        }}
                      >
                        Enregistrer les modifications
                  </Button>
                    </Box>
                  </form>
                </Box>
                
                <Box sx={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.1)', 
                  borderRadius: { xs: '8px', md: '16px' }, 
                  p: { xs: 2, md: 3 },
                  border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      fontWeight: 600, 
                      mb: { xs: 2, md: 3 },
                      fontSize: { xs: '1rem', md: '1.125rem' },
                      color: '#fff'
                    }}
                  >
                    Changer votre mot de passe
                  </Typography>
                  
                  <form onSubmit={(e) => {
                    e.preventDefault();
                    
                    // Get password values
                    const currentPassword = e.target.currentPassword.value;
                    const newPassword = e.target.newPassword.value;
                    const confirmPassword = e.target.confirmPassword.value;
                    
                    // Simple validation
                    if (!currentPassword || !newPassword || !confirmPassword) {
                      setPasswordError('Veuillez remplir tous les champs');
                      return;
                    }
                    
                    if (newPassword !== confirmPassword) {
                      setPasswordError('Les nouveaux mots de passe ne correspondent pas');
                      return;
                    }
                    
                    if (newPassword.length < 6) {
                      setPasswordError('Le mot de passe doit contenir au moins 6 caractères');
                      return;
                    }
                    
                    // 🔐 SIMULATION DE CHANGEMENT DE MOT DE PASSE
                    setPasswordError('');
                    setShowSuccessMessage(true);
                    setTimeout(() => setShowSuccessMessage(false), 3000);
                    
                    // Clear form
                    e.target.reset();
                    
                    // Show success message
                    setPasswordError('');
                    setShowSuccessMessage(true);
                    setTimeout(() => setShowSuccessMessage(false), 3000);
                  }}>
                    <Box sx={{ mb: { xs: 2.5, md: 3 } }}>
                                              <InputLabel 
                          htmlFor="currentPassword" 
                          sx={{ 
                            mb: 0.5, 
                            fontWeight: 500, 
                            color: 'rgba(255, 255, 255, 0.7)',
                            fontSize: { xs: '0.75rem', md: '0.875rem' }
                          }}
                      >
                        Mot de passe actuel
                      </InputLabel>
                      <input
                        id="currentPassword"
                        name="currentPassword"
                        type="password"
                        autoComplete="current-password"
                        style={{
                          width: '100%',
                          padding: '14px 16px',
                          borderRadius: '8px',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          fontFamily: 'inherit',
                          fontSize: '16px',
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          color: '#fff',
                          outline: 'none'
                        }}
                        placeholder="Entrez votre mot de passe actuel"
                      />
                </Box>
                    
                    <Box sx={{ mb: { xs: 2.5, md: 3 } }}>
                                              <InputLabel 
                          htmlFor="newPassword" 
                          sx={{ 
                            mb: 0.5, 
                            fontWeight: 500, 
                            color: 'rgba(255, 255, 255, 0.7)',
                            fontSize: { xs: '0.75rem', md: '0.875rem' }
                          }}
                      >
                        Nouveau mot de passe
                      </InputLabel>
                      <input
                        id="newPassword"
                        name="newPassword"
                        type="password"
                        autoComplete="new-password"
                        style={{
                          width: '100%',
                          padding: '14px 16px',
                          borderRadius: '8px',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          fontFamily: 'inherit',
                          fontSize: '16px',
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          color: '#fff',
                          outline: 'none'
                        }}
                        placeholder="Entrez votre nouveau mot de passe"
                      />
                    </Box>
                    
                    <Box sx={{ mb: { xs: 2.5, md: 3 } }}>
                                              <InputLabel 
                          htmlFor="confirmPassword" 
                          sx={{ 
                            mb: 0.5, 
                            fontWeight: 500, 
                            color: 'rgba(255, 255, 255, 0.7)',
                            fontSize: { xs: '0.75rem', md: '0.875rem' }
                          }}
                      >
                        Confirmer le nouveau mot de passe
                      </InputLabel>
                      <input
                        id="confirmPassword"
                        name="confirmPassword"
                        type="password"
                        autoComplete="new-password"
                        style={{
                          width: '100%',
                          padding: '14px 16px',
                          borderRadius: '8px',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          fontFamily: 'inherit',
                          fontSize: '16px',
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          color: '#fff',
                          outline: 'none'
                        }}
                        placeholder="Confirmez votre nouveau mot de passe"
                      />
          </Box>
                    
                    {passwordError && (
                      <Alert severity="error" sx={{ mb: 3 }}>
                        {passwordError}
                      </Alert>
                    )}
                    
                    <Box sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-end' } }}>
          <Button 
                        type="submit"
            variant="contained"
            sx={{ 
                          px: { xs: 4, md: 3 },
                          py: { xs: 1.5, md: 1 },
              borderRadius: '8px',
                          fontWeight: 600,
                          minWidth: { xs: '200px', md: 'auto' },
                          fontSize: { xs: '0.9375rem', md: 'inherit' },
                          background: 'linear-gradient(to right, #2563eb, #9333ea)',
                          '&:hover': {
                            background: 'linear-gradient(to right, #1e40af, #7e22ce)',
                          }
            }}
          >
                        Changer le mot de passe
          </Button>
        </Box>
                  </form>
                </Box>
              </Box>
            </Box>
          </Box>
        </TabPanel>

        <Divider />
      </Card>

      {/* Success notification */}
      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={3000}
        onClose={() => setShowSuccessMessage(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" sx={{ borderRadius: '12px' }}>
          Paramètres mis à jour avec succès !
        </Alert>
      </Snackbar>

      {/* App info */}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)' }}>
          TravelIT v{settings?.appVersion || '1.0.0'} — {settings?.appCopyright || '© 2024 TravelIT'}
        </Typography>
      </Box>
    </Box>
  );
};

export default Parametres; 